<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useProductStore } from '@/stores/product';
import { useI18n } from 'vue-i18n';
import { useFavoritesStore } from '@/stores/favorites';
import { addToWishlistFB } from '@/api/fb';
import { set } from 'lodash';

const { t, locale } = useI18n()

const favoritesStore = useFavoritesStore()
const ProductStore = useProductStore()
const product = ref<any | null>(null);
const loaded = ref(false);
const article = ref<any | null>(null);
const reloadTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const retryStart = ref(Date.now());
const maxLoading = 5;
const maxTimeout = ref(false)
// we show skeleton only if not loaded and retryStart is less than maxLoading in seconds
const showSkeleton = computed(() => !loaded.value && !maxTimeout.value);

const props = defineProps({
    id: {
        type: [String , Number],
        required: false
    },
    article_id: {
        type: [String , Number],
        required: false
    },
    sku: {
        type: String,
        required: false
    },
    title: {
        type: String,
        required: false
    },
    subtitle: {
        type: String,
        required: false
    },
    img: {
        type: String,
        required: false
    },
    variation: {
        type: String,
        required: false
    }
})

const img = computed(()=>{
    if(props.img) return props.img
    // we check if the product has image and if so we build up the url
    if(product.value && product.value.images && product.value.images[0]){
        // https://assets.zokkus.com/images/format-579x300/KW-Revolution-Professional-ND-Kit-29594_1669677003.jpg)
        return `https:///assets.zokkus.com/images/format-579x300/${product.value.images[0].name}`
    }

    return '/img/no-picture.jpg';
})
const title = computed(()=>{
    if(props.title) return props.title
    if(product.value && product.value.content && product.value.content[locale.value]){
        let title = product.value.content[locale.value].name
        if(article.value && article.value.name!=''){
            title += ` - ${article.value.name}`
            return title
        }
        if(product.value.articles.length == 1 && product.value.articles[0].name!=''){
            title += ` - ${product.value.articles[0].name}`
            return title
        }
        return title
    }
    return ''
})
const subtitle = computed(()=>{
    if(props.subtitle) return props.subtitle
    return ''
})
const link = computed(()=>{
    if(article.value){
        return `/${locale.value}/product/${product.value.slug}_${product.value.id}?variation=${article.value.name}`
    }
    if(product.value){
        if(props.variation && props.variation != ''){
            return `/${locale.value}/product/${product.value.slug}_${product.value.id}?variation=${props.variation}`
        }
        else{
            return `/${locale.value}/product/${product.value.slug}_${product.value.id}`
        }
    }
    return ''
})
const price = computed(()=>{
    if(article.value && article.value.prices.discount){
        return article.value.prices.discount.toFixed(2).replace('.',',');
    }
    if(product.value && product.value.prices){
        // we display price with 2 decimals and a comma as separator
        return product.value.prices.discount.toFixed(2).replace('.',',');
    }
    return '--,--';
})
const official = computed(()=>{
    if(article.value && article.value.prices.official && article.value.prices.official > article.value.prices.discount){
        return article.value.prices.official.toFixed(2).replace('.',',');
    }
    if(product.value && product.value.prices.official && product.value.prices.official > product.value.prices.discount){
        return product.value.prices.official.toFixed(2).replace('.',',');
    }
    return false
})
const startingPrice = computed(()=>{
    // we check the article price and if one has a price higher than product price then we return true otherwise false
    if(article.value){
        return false;
    }
    if(product.value && product.value.prices.discount){
    const p = product.value.articles.find((art: any)=>art.prices.discount > product.value.prices.discount)
        if(p) return true
    }
    return false
})
function loadProduct(){
    // we set retryStart to the current unix timestamp
    retryStart.value = Date.now();
    setTimeout(() => {
        maxTimeout.value = true
    }, maxLoading * 1000);
    if(props.id){
    ProductStore.getProductById(props.id).then((res: any)=>{
            product.value = res
            if(props.article_id){
                article.value = res.articles.find((art:any)=>art.id == props.article_id)
            }
            if(res && res.reload ){
                // we try to reload the product in 1s
                reloadTimeout.value = setTimeout(() => {
                    loadProduct()
                }, 2000)
                loaded.value = false
            } else if(res && !res.reload) {
                loaded.value = true
            } else {
                // exceeded max retries -> stop showing skeleton
                loaded.value = false
            }
        })
    }else if(props.sku){
    ProductStore.getProductBySku(props.sku).then((res: any)=>{
            product.value = res
            if(res && res.reload ){
                // we try to reload the product in 5s
                reloadTimeout.value = setTimeout(() => {
                    loadProduct()
                }, 2000)
                loaded.value = false
            } else if(res && !res.reload) {
                loaded.value = true
            } else {
                loaded.value = false
            }
        })
    }
}
onMounted(()=>{
    loadProduct()
})
onUnmounted(()=>{
    if(reloadTimeout && reloadTimeout.value){
        clearTimeout(reloadTimeout.value)
    }
})
  
function toggleFavorite(event: MouseEvent){
    event.preventDefault()
    if(product.value){
        // we check if the product is already in the favorites
        if(!favoritesStore.isFavorite(product.value.id)){
            // we add the product to the favorites
            addToWishlistFB(product.value)
        }
        favoritesStore.toogleFavorite(product.value.id)
    }
}
</script>
<template>
    <router-link :to="link" class="product-preview" v-if="loaded">
            <div class="product-preview-img">
                <img :src="img" :alt="title" />
            </div>
            <div class="product-preview-content">
                <h4>{{title}}</h4>
                <p v-if="subtitle!=''">{{subtitle}}</p>
            </div>
            <div class="product-price">
                <div v-if="startingPrice" class="starting-price">{{ t('product.starting_price') }}</div>
                <span>{{price}} €</span>
            </div>
            <div v-if="official" class="product-official">
                {{official}} €
            </div>
            <v-btn class="product-star" icon variant="text" @click="toggleFavorite" :class="{active: favoritesStore.isFavorite(product.id)}">
                <v-icon>{{favoritesStore.isFavorite(product.id)? 'mdi-star':'mdi-star-outline'}}</v-icon>
            </v-btn>
    </router-link>
    <div v-else-if="showSkeleton" class="product-preview skeleton">
        <div class="product-preview-img">
            <v-skeleton-loader type="image" class="s-image" />
        </div>
        <div class="product-preview-content">
            
        </div>
    </div>
    
</template>
<style lang="scss" scoped>
.product-preview{
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none;
    width: 308px;
    height: 278px;
    color: #000;
    background: #FFFFFF;
    box-shadow: 0px 0px 8px 0px #00000040;
    border-radius: 11px;
    z-index: 1;
    padding-bottom: 8px;
    &:after{
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 8px;
        width: 100%;
        border-radius: 0px 0px 11px 11px;
        content:'';
        background-color: #F52B2B;
        z-index: 2;
        
    }
    &:hover{
        // we change the shadow color on hover
        box-shadow: 0px 0px 12px 0px #000000CC;
    }
    .product-star{
        position: absolute;
        top: 0;
        left: 0;
        color: #C9C9C9;
        text-shadow: 0px 0px 8px 0px #00000026;
        z-index: 3;
        transition: all 0.3s;
        &:hover{
            color: #F52B2B;
        }
        &.active{
            color: #F52B2B;
            &:hover{
                color: #b8bb03;
            }
        }
    }
    .product-preview-img{
        width: 100%;
        height: calc(100% - 60px);
        position: relative;
        img{
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }
    .product-preview-content{
        padding: 10px;
        padding-bottom: 18px;
        width: 100%;
        height: 60px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: linear-gradient(180deg, #111F33 20.65%, #222222 100%);
        h4{
            font-family: Montserrat;
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: 17.07px;
            text-align: left;
            text-underline-position: from-font;
            text-decoration-skip-ink: none;
            color: #FFFFFF;
        }
        p{
            font-family: Montserrat;
            width: 100%;
            font-size: 12px;
            font-weight: 400;
            line-height: 14.63px;
            text-align: left;
            text-underline-position: from-font;
            text-decoration-skip-ink: none;
            color: #FFFFFF;
        }
    }
    .product-starting-price{
        position: absolute;
        top: 0;
        left: 0;
        right: 106px;
        height: 35px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }
    .product-price{
        position: absolute;
        top: 0;
        right: 0;
        background: #F52B2B;
        width: 102px;
        height: 35px;
        border-radius: 0px 8px 0px 8px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-family: Montserrat;
        font-size: 18px;
        font-weight: 600;
        line-height: 19px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #FFFFFF;
        .starting-price{
            padding: 0 5px;
            font-size: 12px;
            font-weight: 400;
            line-height: 13px;
            width: 100%;
            text-align: left;
        }
    }
    .product-official{
        position: absolute;
        top: 39px;
        right: 0;
        width: 102px;
        height: 35x;
        display: flex;
        justify-content: center;
        align-items: center;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        line-height: 17.07px;
        text-align: left;
        text-decoration-line: line-through;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #000000;

    }
}
.skeleton{
    .s-image{
        width: 100%;
        height: 100%;
        border-radius: 8px;
    }
    .s-text{
        width: 90%;
        height: 12px;
        &.short{ width: 70%; }
    }
    .s-price{
        width: 60%;
        height: 14px;
    }
}
</style>